/**
 * WorkflowEngine — Step-by-step workflow executor with pipeline
 *
 * Executes workflow steps sequentially via next[] pointers (supports branching).
 * Each step output merges into pipelineData so later steps can access earlier outputs.
 * Supports {{variable}} interpolation, per-step error handling, retry, and timeouts.
 */

import type {
  EngineEvent,
  EngineWorkflow,
  EngineStep,
  EngineExecutionContext,
  StepExecutionResult,
  StepExecutor,
  StepLogEntry,
  TriggerCondition,
} from './types';
import { evaluateCondition } from './triggerMatcher';

/** Registry of step executors by type */
const executorRegistry: Map<string, StepExecutor> = new Map();

/**
 * Register a step executor
 */
export function registerStepExecutor(executor: StepExecutor): void {
  executorRegistry.set(executor.type, executor);
}

/**
 * Get all registered executor types
 */
export function getRegisteredExecutorTypes(): string[] {
  return Array.from(executorRegistry.keys());
}

/**
 * Generate a unique execution ID
 */
function generateExecutionId(): string {
  return `exec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Interpolate {{variable}} placeholders in a value using pipelineData
 */
function interpolateValue(value: unknown, data: Record<string, unknown>): unknown {
  if (typeof value === 'string') {
    // Full replacement: if entire string is a single {{path}}, return the raw value
    const fullMatch = value.match(/^\{\{(.+?)\}\}$/);
    if (fullMatch) {
      return resolveDataPath(fullMatch[1], data);
    }
    // Partial replacement: replace all {{path}} within the string
    return value.replace(/\{\{(.+?)\}\}/g, (_, path) => {
      const resolved = resolveDataPath(path.trim(), data);
      return resolved !== undefined ? String(resolved) : '';
    });
  }
  if (Array.isArray(value)) {
    return value.map((item) => interpolateValue(item, data));
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = interpolateValue(v, data);
    }
    return result;
  }
  return value;
}

/**
 * Resolve a dot-notation path against the pipeline data
 */
function resolveDataPath(path: string, data: Record<string, unknown>): unknown {
  const parts = path.split('.');
  let current: unknown = data;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Execute a single step with timeout
 */
async function executeStepWithTimeout(
  executor: StepExecutor,
  step: EngineStep,
  context: EngineExecutionContext,
  timeoutMs: number
): Promise<StepExecutionResult> {
  return Promise.race([
    executor.execute(step, context),
    new Promise<StepExecutionResult>((_, reject) =>
      setTimeout(() => reject(new Error(`Step "${step.name}" timed out after ${timeoutMs}ms`)), timeoutMs)
    ),
  ]);
}

/**
 * Execute a workflow triggered by an event
 */
export async function executeWorkflow(
  workflow: EngineWorkflow,
  triggerEvent: EngineEvent
): Promise<EngineExecutionContext> {
  const now = new Date().toISOString();

  // Seed pipeline data from trigger event
  const pipelineData: Record<string, unknown> = {
    event: triggerEvent,
    entityType: triggerEvent.entityType,
    entityId: triggerEvent.entityId,
    userId: triggerEvent.userId,
    ...triggerEvent.data,
  };

  const context: EngineExecutionContext = {
    executionId: generateExecutionId(),
    workflow,
    triggerEvent,
    currentStepId: null,
    stepOutputs: {},
    pipelineData,
    status: 'running',
    log: [],
    startedAt: now,
  };

  // Build a map of steps by ID
  const stepsById = new Map<string, EngineStep>();
  for (const step of workflow.steps) {
    stepsById.set(step.id, step);
  }

  // Start with the first step
  if (workflow.steps.length === 0) {
    context.status = 'completed';
    context.completedAt = new Date().toISOString();
    return context;
  }

  // BFS execution via next[] pointers
  const queue: string[] = [workflow.steps[0].id];
  const visited = new Set<string>();

  while (queue.length > 0 && context.status === 'running') {
    const stepId = queue.shift()!;
    if (visited.has(stepId)) continue;
    visited.add(stepId);

    const step = stepsById.get(stepId);
    if (!step) {
      addLog(context, stepId, 'unknown', 'error', undefined, `Step "${stepId}" not found`);
      context.status = 'failed';
      break;
    }

    context.currentStepId = stepId;

    // Check step condition
    if (step.condition) {
      const conditionEvent: EngineEvent = {
        ...triggerEvent,
        data: context.pipelineData,
      };
      if (!evaluateCondition(step.condition as TriggerCondition, conditionEvent)) {
        addLog(context, step.id, step.name, 'skipped');
        // Still follow next pointers
        for (const nextId of step.next) {
          queue.push(nextId);
        }
        continue;
      }
    }

    // Find executor
    const executor = executorRegistry.get(step.type);
    if (!executor) {
      addLog(context, step.id, step.name, 'error', undefined, `No executor for type "${step.type}"`);
      if (step.onError === 'stop') {
        context.status = 'failed';
        break;
      }
      for (const nextId of step.next) queue.push(nextId);
      continue;
    }

    // Interpolate step config
    const interpolatedStep: EngineStep = {
      ...step,
      config: interpolateValue(step.config, context.pipelineData) as Record<string, unknown>,
    };

    // Execute with retry logic
    const result = await executeWithRetry(executor, interpolatedStep, context, step);

    if (result.success) {
      addLog(context, step.id, step.name, 'success', result.output);

      // Merge output into pipeline
      if (result.output) {
        context.stepOutputs[step.id] = result.output;
        Object.assign(context.pipelineData, result.output);
      }

      // Determine next steps
      const nextIds = result.nextStepIds || step.next;
      for (const nextId of nextIds) {
        queue.push(nextId);
      }
    } else {
      addLog(context, step.id, step.name, 'error', undefined, result.error);

      switch (step.onError) {
        case 'stop':
          context.status = 'failed';
          break;
        case 'continue':
          for (const nextId of step.next) queue.push(nextId);
          break;
        // 'retry' is handled inside executeWithRetry
      }
    }
  }

  // Finalize
  if (context.status === 'running') {
    context.status = 'completed';
  }
  context.currentStepId = null;
  context.completedAt = new Date().toISOString();

  return context;
}

/**
 * Execute a step with retry logic
 */
async function executeWithRetry(
  executor: StepExecutor,
  interpolatedStep: EngineStep,
  context: EngineExecutionContext,
  originalStep: EngineStep
): Promise<StepExecutionResult> {
  const maxAttempts = originalStep.onError === 'retry' ? Math.max(1, originalStep.retryCount) : 1;
  const timeoutMs = originalStep.timeout || 60000;

  let lastResult: StepExecutionResult = { success: false, error: 'No execution attempted' };

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const startTime = Date.now();

    try {
      lastResult = await executeStepWithTimeout(executor, interpolatedStep, context, timeoutMs);

      if (lastResult.success) {
        return lastResult;
      }

      if (attempt < maxAttempts) {
        addLog(context, originalStep.id, originalStep.name, 'retrying', undefined, lastResult.error, attempt);
        // Exponential backoff: 1s, 2s, 4s, ...
        await sleep(Math.min(1000 * Math.pow(2, attempt - 1), 10000));
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      lastResult = { success: false, error: errorMsg };

      if (attempt < maxAttempts) {
        addLog(context, originalStep.id, originalStep.name, 'retrying', undefined, errorMsg, attempt);
        await sleep(Math.min(1000 * Math.pow(2, attempt - 1), 10000));
      }
    }
  }

  return lastResult;
}

function addLog(
  context: EngineExecutionContext,
  stepId: string,
  stepName: string,
  status: StepLogEntry['status'],
  output?: unknown,
  error?: string,
  attempt?: number
): void {
  context.log.push({
    stepId,
    stepName,
    status,
    timestamp: new Date().toISOString(),
    output,
    error,
    attempt,
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
